import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { font_sizes } from '@hookdeck/theme';
import MonacoEditor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

import useOnClickOutside from '../hooks/useOnClickOutside';
import Divider from './base/Divider';
import Text from './base/Text';
import { Div, StyledUtilsProps } from './helpers/StyledUtils';

export type EditorLanguage = 'json' | 'javascript' | 'typescript' | 'text' | 'yaml';
export type EditorTypeDeclarations = 'transformation';

interface Props extends StyledUtilsProps {
  value: string;
  error?: string;
  height?: string;
  padding?: number;
  scroll?: boolean;
  small_text?: boolean;
  language?: EditorLanguage;
  display?: 'minimal' | 'full';
  prevent_theme_change?: boolean;
  type_declarations?: EditorTypeDeclarations;
  format_on_blur?: boolean;
  disabled?: boolean;
  onChange?: (v: string) => void;
  cmdEnter?: () => void;
  onMount?: (editor: editor.IStandaloneCodeEditor) => void;
  // Rendered in a footer below the editor, next to the language label.
  footer_action?: React.ReactNode;
  // Rendered in the footer, below the action.
  footer_content?: React.ReactNode;
  // Shown while the editor is empty.
  placeholder?: string;
  options?: Partial<editor.IEditorConstructionOptions>;
  // Non-editable text shown at the end of the line holding each offset, styled like a
  // type in the event viewer (italic, muted). With `replace`, it is shown instead of the
  // `replace` characters at the offset. Recomputed as the content changes.
  inline_hints?: (text: string) => { offset: number; text: string; replace?: number }[];
}

const options = {
  minimal: {
    lineDecorationsWidth: '0ch',
    renderLineHighlightOnlyWhenFocus: true,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    lineNumbers: 'off' as any,
  },
  full: {},
};

const EDITOR_TYPE_DECLARATIONS: Record<
  EditorTypeDeclarations,
  { source_urls: string[]; virtual_path: string }
> = {
  transformation: {
    source_urls: ['/transformation-editor.d.ts', '/transformation-editor-runtime.d.ts'],
    virtual_path: 'file:///transformation-editor.d.ts',
  },
};

const type_declaration_promises = new Map<EditorTypeDeclarations, Promise<string>>();

const getTypeDeclarations = (type_declarations: EditorTypeDeclarations): Promise<string> => {
  const existing_promise = type_declaration_promises.get(type_declarations);
  if (existing_promise) return existing_promise;

  const declaration = EDITOR_TYPE_DECLARATIONS[type_declarations];
  const declaration_promise = Promise.all(
    declaration.source_urls.map((source_url) =>
      fetch(source_url).then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to load editor type declarations (${response.status} ${response.statusText})`,
          );
        }
        return response.text();
      }),
    ),
  )
    .then((sources) => sources.join('\n'))
    .catch((error) => {
      type_declaration_promises.delete(type_declarations);
      throw error;
    });
  type_declaration_promises.set(type_declarations, declaration_promise);
  return declaration_promise;
};

const StyledHeightWrapper = styled.div<{ height?: string }>`
  .hd-editor-inline-hint {
    font-style: italic;
    color: ${({ theme }) => theme.colors.on.neutral.secondary_neutral} !important;
  }
  .hd-editor-replaced-text {
    display: none;
  }
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 1px;
  ${({ height }) => height && `height: ${height};`}
  > section {
    flex-grow: 1;
    height: unset !important;
  }
  > div {
    position: sticky;
    position: absolute;
    right: 0;
    bottom: 0;
  }
`;

const EditorLanguageLabel: React.FC<{ language: EditorLanguage }> = ({ language }) => (
  <Text size="s" muted>
    {language.toUpperCase()}
  </Text>
);

const Editor: React.FC<Props> = ({
  scroll,
  value,
  error,
  height,
  language,
  display,
  prevent_theme_change,
  type_declarations,
  format_on_blur,
  disabled,
  small_text,
  onChange,
  cmdEnter,
  onMount,
  footer_action,
  footer_content,
  placeholder,
  options: params_options,
  inline_hints,
}) => {
  // 'use no memo' — the focus guard below reads the live editor instance during
  // render, which must never be memoized away by React Compiler.
  'use no memo';
  language = language || 'json';
  const theme = useTheme();

  const editor_ref = useRef<any>(null);
  const [mounted, setMounted] = useState<{
    editor: editor.IStandaloneCodeEditor;
    monaco: typeof import('monaco-editor');
  } | null>(null);

  const inline_hints_ref = useRef(inline_hints);
  inline_hints_ref.current = inline_hints;
  const has_inline_hints = !!inline_hints;
  useEffect(() => {
    if (!mounted || !has_inline_hints) {
      return;
    }
    const { editor: mounted_editor, monaco } = mounted;
    const decorations = mounted_editor.createDecorationsCollection();
    const update = () => {
      const model = mounted_editor.getModel();
      if (!model || !inline_hints_ref.current) return;
      decorations.set(
        inline_hints_ref.current(model.getValue()).map((hint) => {
          if (hint.replace) {
            const start = model.getPositionAt(hint.offset);
            const end = model.getPositionAt(hint.offset + hint.replace);
            return {
              range: new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column),
              options: {
                inlineClassName: 'hd-editor-replaced-text',
                before: { content: hint.text, inlineClassName: 'hd-editor-inline-hint' },
              },
            };
          }
          const line = model.getPositionAt(hint.offset).lineNumber;
          const column = model.getLineMaxColumn(line);
          return {
            range: new monaco.Range(line, column, line, column),
            options: {
              showIfCollapsed: true,
              after: { content: hint.text, inlineClassName: 'hd-editor-inline-hint' },
            },
          };
        }),
      );
    };
    update();
    const listener = mounted_editor.onDidChangeModelContent(update);
    return () => {
      listener.dispose();
      decorations.clear();
    };
  }, [mounted, has_inline_hints]);

  // Monaco has no placeholder option in this version: render it as a content widget at the
  // first position, with the editor font, so it lines up exactly with the cursor.
  useEffect(() => {
    if (!mounted || !placeholder) {
      return;
    }
    const { editor: mounted_editor, monaco } = mounted;
    const font_info = mounted_editor.getOption(monaco.editor.EditorOption.fontInfo);
    const getWidth = (layout: editor.EditorLayoutInfo) =>
      `${layout.contentWidth - layout.verticalScrollbarWidth}px`;
    const node = document.createElement('div');
    node.textContent = placeholder;
    Object.assign(node.style, {
      fontFamily: font_info.fontFamily,
      fontSize: `${font_info.fontSize}px`,
      fontWeight: font_info.fontWeight,
      letterSpacing: `${font_info.letterSpacing}px`,
      lineHeight: `${font_info.lineHeight}px`,
      color: theme.colors.on.neutral.tertiary_neutral,
      pointerEvents: 'none',
      whiteSpace: 'pre-wrap',
      width: getWidth(mounted_editor.getLayoutInfo()),
    });
    const widget: editor.IContentWidget = {
      getId: () => 'hookdeck.placeholder',
      getDomNode: () => node,
      getPosition: () => ({
        position: { lineNumber: 1, column: 1 },
        preference: [monaco.editor.ContentWidgetPositionPreference.EXACT],
      }),
    };
    let is_shown = false;
    const update = () => {
      const is_empty = mounted_editor.getValue() === '';
      if (is_empty && !is_shown) {
        mounted_editor.addContentWidget(widget);
      } else if (!is_empty && is_shown) {
        mounted_editor.removeContentWidget(widget);
      }
      is_shown = is_empty;
    };
    update();
    const listener = mounted_editor.onDidChangeModelContent(update);
    const layout_listener = mounted_editor.onDidLayoutChange((layout) => {
      node.style.width = getWidth(layout);
    });
    return () => {
      listener.dispose();
      layout_listener.dispose();
      is_shown && mounted_editor.removeContentWidget(widget);
    };
  }, [mounted, placeholder, theme]);
  const wrapper_ref = useOnClickOutside(() => {
    if (editor_ref?.current) {
      format_on_blur && editor_ref.current!.getAction('editor.action.formatDocument')?.run();
    }
  });

  // While the user is typing (editor focused), never let an externally-pushed value
  // rewrite the buffer under the caret: @monaco-editor/react applies a controlled
  // value as a full-text replace, which snaps the cursor to the end of the content
  // and reads as the editor losing focus. External updates (formik setValues from a
  // URL sync, resets, etc.) re-apply on the first render after the editor blurs.
  const editor = editor_ref.current;
  const controlled_value =
    onChange && editor?.hasTextFocus?.() && typeof value === 'string'
      ? editor.getValue()
      : value === undefined
        ? ''
        : value;

  return (
    <>
      {/* With a footer, the editor fills the space left by the footer in a flex column. */}
      <StyledHeightWrapper
        height={height || (footer_action ? undefined : '100%')}
        ref={wrapper_ref}>
        <MonacoEditor
          defaultLanguage={language}
          onChange={onChange}
          theme={
            !onChange && !prevent_theme_change
              ? 'my-theme'
              : theme.mode === 'dark'
                ? 'dark'
                : 'light'
          }
          value={controlled_value}
          width="100%"
          height="100px"
          onMount={(editor, monaco) => {
            if (language !== 'text') {
              monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: true,
                noSyntaxValidation: false,
                // "File is a CommonJS module; it may be converted to an ES module":
                // transformations support require() and import alike.
                diagnosticCodesToIgnore: [80001],
              });
              // compiler options
              monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.ES2015,
                allowNonTsExtensions: true,
                allowJs: true,
              });
            }

            if (type_declarations) {
              const declaration = EDITOR_TYPE_DECLARATIONS[type_declarations];
              getTypeDeclarations(type_declarations)
                .then((content) => {
                  monaco.languages.typescript.javascriptDefaults.addExtraLib(
                    content,
                    declaration.virtual_path,
                  );
                })
                .catch((declaration_error) => {
                  console.error(declaration_error);
                });
            }

            if (cmdEnter) {
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, cmdEnter);
              editor.addCommand(monaco.KeyMod.WinCtrl | monaco.KeyCode.Enter, cmdEnter);
            }

            if (!onChange && !prevent_theme_change) {
              monaco.editor.defineTheme('my-theme', {
                base: theme.mode === 'dark' ? 'vs-dark' : 'vs',
                inherit: true,
                rules: [
                  { token: 'type', foreground: theme.colors.on.neutral.primary_neutral },
                  { token: 'number', foreground: theme.colors.on.code.number },
                  { token: 'string.yaml', foreground: theme.colors.on.code.string },
                ],
                colors: {
                  'editor.background': theme.colors.surface.base.variant_surface_3,
                },
              });
              monaco.editor.setTheme('my-theme');
            } else if (theme.mode === 'dark') {
              monaco.editor.defineTheme('dark', {
                base: 'vs-dark',
                inherit: true,
                rules: [],
                colors: {
                  'editor.background': theme.colors.surface.base.surface,
                },
              });
              monaco.editor.setTheme('dark');
            } else {
              monaco.editor.defineTheme('light', {
                base: 'vs',
                inherit: true,
                rules: [],
                colors: {
                  'editor.background': theme.colors.surface.base.surface,
                },
              });
              monaco.editor.setTheme('light');
            }

            editor?.getAction('editor.action.formatDocument')?.run();
            editor_ref.current = editor;
            setMounted({ editor, monaco });
            onMount?.(editor);
          }}
          options={{
            padding: {
              top: 12,
              bottom: 12,
            },
            automaticLayout: true,
            // Suggestions and hovers render outside the editor instead of being clipped by it.
            fixedOverflowWidgets: true,
            fontLigatures: true,
            tabSize: 2,
            readOnly: !onChange || disabled,
            domReadOnly: !onChange || disabled,
            guides: {
              indentation: !!onChange,
              highlightActiveIndentation: !!onChange,
            },
            minimap: {
              enabled: false,
            },
            scrollbar: {
              vertical: 'hidden',
              horizontal: 'hidden',
              handleMouseWheel: scroll === false ? false : true,
              alwaysConsumeMouseWheel: scroll === false ? false : true,
            },
            matchBrackets: !onChange ? 'never' : 'always',
            scrollBeyondLastColumn: 0,
            fontSize: small_text ? font_sizes.xs.font_size : font_sizes.s.font_size,
            fontFamily: 'JetBrains Mono, monospace',
            scrollBeyondLastLine: scroll === false ? false : true,
            ...options[display || 'minimal'],
            ...params_options,
          }}
        />
        {onChange && !footer_action && (
          <>
            <Div
              p={{ x: 4, y: 2 }}
              flex={{ justify: 'flex-end', align: 'end' }}
              style={{ position: 'absolute', left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
              <EditorLanguageLabel language={language} />
            </Div>
          </>
        )}
      </StyledHeightWrapper>
      {footer_action && (
        <>
          <Divider />
          <Div p={{ x: 3, y: 2 }} flex={{ direction: 'column', gap: 2 }}>
            <Div flex={{ justify: 'space-between', align: 'center', gap: 3 }}>
              {footer_action}
              <EditorLanguageLabel language={language} />
            </Div>
            {footer_content}
          </Div>
        </>
      )}
      {error && typeof error === 'string' && error.trim().length > 0 && (
        <>
          <Divider />
          <Div p={{ x: 3, y: 2 }}>
            <Text as="span" danger>
              {error}
            </Text>
          </Div>
        </>
      )}
    </>
  );
};

export default Editor;
